<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import FupFlowsAPI from 'dashboard/api/fupFlows';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const flows = ref([]);
const isFetching = ref(false);
const isDeletingId = ref(null);
const showDeletePopup = ref(false);
const selectedFlow = ref(null);

const fetchFlows = async () => {
  isFetching.value = true;
  try {
    const { data } = await FupFlowsAPI.getFlows();
    flows.value = Array.isArray(data) ? data : [];
  } catch {
    useAlert(t('FUP.LIST.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openBuilder = flowId => {
  router.push(accountScopedRoute('fup_flow_builder', { flowId }));
};

const openDeletePopup = flow => {
  selectedFlow.value = flow;
  showDeletePopup.value = true;
};

const closeDeletePopup = () => {
  showDeletePopup.value = false;
  selectedFlow.value = null;
};

const confirmDeletion = async () => {
  const flow = selectedFlow.value;
  if (!flow) return;
  isDeletingId.value = flow.id;
  closeDeletePopup();
  try {
    await FupFlowsAPI.deleteFlow(flow.id);
    flows.value = flows.value.filter(item => item.id !== flow.id);
  } catch {
    useAlert(t('FUP.LIST.DELETE_ERROR'));
  } finally {
    isDeletingId.value = null;
  }
};

onMounted(fetchFlows);
</script>

<template>
  <section class="flex flex-col flex-1 min-h-0 bg-n-background">
    <header
      class="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-n-weak"
    >
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('FUP.LIST.TITLE') }}
      </h1>
      <span class="ms-auto" />
      <Button size="sm" icon="i-lucide-plus" @click="openBuilder('new')">
        {{ t('FUP.LIST.NEW') }}
      </Button>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else-if="!flows.length"
      class="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center"
    >
      <span class="i-lucide-workflow text-4xl text-n-slate-9" />
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('FUP.LIST.EMPTY_TITLE') }}
      </h2>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('FUP.LIST.EMPTY_DESCRIPTION') }}
      </p>
      <Button size="sm" @click="openBuilder('new')">
        {{ t('FUP.LIST.NEW') }}
      </Button>
    </div>

    <ul v-else class="flex-1 p-4 overflow-y-auto divide-y divide-n-weak">
      <li
        v-for="flow in flows"
        :key="flow.id"
        class="flex items-center gap-3 py-3"
      >
        <div class="flex-1 min-w-0">
          <button
            class="text-sm font-medium truncate text-n-slate-12 hover:underline"
            @click="openBuilder(flow.id)"
          >
            {{ flow.name }}
          </button>
          <p class="text-xs text-n-slate-11">
            {{
              t('FUP.LIST.STEPS_COUNT', {
                n: (flow.steps || []).length,
              })
            }}
            ·
            {{ flow.active ? t('FUP.LIST.ACTIVE') : t('FUP.LIST.PAUSED') }}
          </p>
        </div>
        <Button
          size="sm"
          color="slate"
          :is-loading="isDeletingId === flow.id"
          @click="openDeletePopup(flow)"
        >
          {{ t('FUP.LIST.DELETE') }}
        </Button>
      </li>
    </ul>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('FUP.LIST.DELETE_TITLE')"
      :message="t('FUP.LIST.DELETE_CONFIRM')"
      :message-value="selectedFlow ? ` ${selectedFlow.name}?` : ''"
      :confirm-text="t('FUP.LIST.DELETE_YES')"
      :reject-text="t('FUP.LIST.DELETE_NO')"
    />
  </section>
</template>
